import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import UserAvatar from "../UserAvatar/UserAvatar";
import "./ChatListItem.css";
import {
  chatCurrentUserIdState,
  chatUnreadCountsState,
  selectedChatRoomIdState,
} from "../../state/chatState";
import {
  formatChatRoomTime,
  getChatRoomAvatarUser,
  getChatRoomDisplayName,
  getChatRoomId,
} from "../../chatRoomUtils";
import { stripChatMarkdown } from "../../chatMarkdownUtils";

const ChatListItem = ({ chat }) => {
  const [, setSearchParams] = useSearchParams();
  const currentUserIdentifier = useRecoilValue(chatCurrentUserIdState);
  const [selectedRoomId, setSelectedRoomId] = useRecoilState(selectedChatRoomIdState);
  const unreadCounts = useRecoilValue(chatUnreadCountsState);
  const roomId = getChatRoomId(chat);
  const isActive = selectedRoomId === roomId;
  const unreadCount = unreadCounts[roomId] || 0;
  const displayName = useMemo(
    () => getChatRoomDisplayName(chat, currentUserIdentifier),
    [chat, currentUserIdentifier],
  );

  const avatarUser = useMemo(
    () => getChatRoomAvatarUser(chat, currentUserIdentifier, displayName),
    [chat, currentUserIdentifier, displayName],
  );

  const formattedLastMessage = useMemo(() => {
    const rawMessage = JSON.parse(chat?.lastMessage || "{}")?.chatMessage;
    return stripChatMarkdown(rawMessage);
  }, [chat?.lastMessage]);

  const handleSelect = useCallback(() => {
    setSelectedRoomId(roomId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("roomId", roomId);
        return next;
      },
      { replace: true },
    );
  }, [roomId, setSearchParams, setSelectedRoomId]);

  return (
    <div className={`chat-list-item ${isActive ? "active" : ""}`}>
      <div
        onClick={handleSelect}
        className="chat-list-item-content"
        style={{
          cursor: "pointer",
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleSelect();
          }
        }}
      >
        <UserAvatar user={avatarUser} size={40} />
        <div className="chat-list-item-info">
          <div className="chat-list-item-header">
            <span className="chat-list-item-name">{displayName}</span>
            <div className="chat-list-item-header-right">
              {unreadCount > 0 ? (
                <span className="chat-list-item-unread" aria-label={`${unreadCount} unread messages`}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              ) : null}
              {chat?.lastMessageAt ? (
                <span className="chat-list-item-time">
                  {formatChatRoomTime(chat.lastMessageAt)}
                </span>
              ) : null}
            </div>
          </div>
          {chat?.lastMessage && (
            <div className="chat-list-item-preview">
              {formattedLastMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatListItem;
